// src/frontend/js/modern/core/queue.js
import { eventBus, EVENTS } from './event-bus.js';
import { AJAX_ACTIONS } from '../shared/constants.js';
import { showError } from '../shared/ui-helpers.js';
import { ScopeHelper } from '../shared/scope-helpers.js';
import { LightQueue } from "../lib/light-queue";

const QUEUE_CONFIG = {
	maxRetries: 3,
	retryDelay: 1000,
	backoff: 'exponential',
	maxDelay: 30000,
	concurrency: 5,
};

const OFFLINE_STORAGE_KEY = 'wlfmc_offline_queue';

class QueueManager {
	constructor() {
		this.queue = null;
		this.initialized = false;
		this._offlineBuffer = [];

		// FIX: Replaced flawed _abortedScopes Set with a taskId tracking system
		this._activeControllers = new Map(); // scope -> AbortController (for in-flight HTTP)
		this._currentTaskId = new Map();     // scope -> unique taskId (for queued tasks)
		this._taskCounter = 0;               // monotonic counter for generating taskIds

		// FIX: Separate in-memory Map for Promise handlers.
		// This prevents JSON.stringify from stripping resolve/reject functions
		// when saving the offline buffer to localStorage.
		this._pendingOfflinePromises = new Map();
	}

	_buildActionMap() {
		// Resolve the mode-aware action name from localized data.
		// Keys MUST match exactly what the legacy code reads from wlfmc_l10n.actions.
		const l10nActions = window.wlfmc_l10n?.actions || {};
		const pick = (constant, key) => l10nActions[key] || constant;
		return {
			// ─── Wishlist ───────────────────────────────────────────
			add_to_wishlist: pick(AJAX_ACTIONS.WISHLIST_ADD, 'add_to_wishlist_action'),
			remove_from_wishlist: pick(AJAX_ACTIONS.WISHLIST_REMOVE, 'remove_from_wishlist_action'),
			delete_item: pick(AJAX_ACTIONS.WISHLIST_DELETE_ITEM, 'delete_item_action'),
			add_to_cart: pick(AJAX_ACTIONS.WISHLIST_ADD_TO_CART, 'add_to_cart_action'),
			sort_items: pick(AJAX_ACTIONS.WISHLIST_SORT, 'sort_wishlist_items'),
			update_item_quantity: pick(AJAX_ACTIONS.WISHLIST_UPDATE_QUANTITY, 'update_item_quantity'),
			change_layout: pick(AJAX_ACTIONS.WISHLIST_CHANGE_LAYOUT, 'change_layout'),
			gdpr_action: pick(AJAX_ACTIONS.GDPR_ACTION, 'gdpr_action'),
			load_automations: pick(AJAX_ACTIONS.LOAD_AUTOMATIONS, 'load_automations'),
			load_fragments: pick('wlfmc_load_fragments', 'load_fragments'),

			// ─── Waitlist ───────────────────────────────────────────
			add_to_waitlist: pick(AJAX_ACTIONS.WAITLIST_ADD, 'add_product_to_waitlist_action'),
			remove_from_waitlist: pick(AJAX_ACTIONS.WAITLIST_REMOVE, 'remove_from_wishlist_action'),

			// ─── Multi-list ─────────────────────────────────────────
			add_to_list: pick(AJAX_ACTIONS.MULTI_LIST_ADD, 'add_product_to_list_action'),
			remove_from_list: pick(AJAX_ACTIONS.MULTI_LIST_REMOVE, 'remove_from_wishlist_action'),
			move_to_list: pick(AJAX_ACTIONS.MULTI_LIST_MOVE, 'move_to_another_list'),
			copy_to_list: pick(AJAX_ACTIONS.MULTI_LIST_COPY, 'copy_to_another_list'),
			create_list: pick(AJAX_ACTIONS.MULTI_LIST_CREATE, 'create_new_list_action'),
			delete_list: pick(AJAX_ACTIONS.MULTI_LIST_DELETE, 'delete_list_action'),
			update_list: pick(AJAX_ACTIONS.MULTI_LIST_UPDATE, 'update_list_action'),
			change_privacy: pick(AJAX_ACTIONS.MULTI_LIST_PRIVACY, 'change_privacy_action'),
			load_lists: pick(AJAX_ACTIONS.MULTI_LIST_LOAD, 'load_lists_action'),

			// ─── Save for Later ─────────────────────────────────────
			add_to_save_for_later: pick(AJAX_ACTIONS.SAVE_FOR_LATER_ADD, 'add_to_save_for_later_action'),
			remove_from_save_for_later: pick(AJAX_ACTIONS.SAVE_FOR_LATER_REMOVE, 'remove_from_save_for_later_action'),
			save_for_later_add_to_cart: pick(AJAX_ACTIONS.SAVE_FOR_LATER_ADD_TO_CART, 'save_for_later_add_to_cart_action'),
			add_all_to_save_for_later: pick(AJAX_ACTIONS.SAVE_FOR_LATER_ADD_ALL, 'add_all_to_save_for_later'),
		};
	}

	init() {
		if (this.initialized) return;
		this.queue = new LightQueue({
			concurrency: QUEUE_CONFIG.concurrency,
			autoStart: true,
		});
		// REMOVED: _setupQueueListeners()
		// Action lifecycle events (started, completed, failed) are now exclusively
		// emitted by ConflictManager with full context (actionId, scope, type).
		// Emitting empty objects from the queue layer caused duplicate/noisy logs.
		this._setupOnlineListeners();
		this._loadOfflineBuffer();
		this.initialized = true;
		console.log('[QueueManager] Initialized with real AbortController support');
	}

	/**
	 * Resolve the correct AJAX URL for a given operation,
	 * matching the legacy architecture exactly.
	 *
	 * @param {string} operation - The operation identifier
	 * @returns {string} The correct AJAX URL
	 */
	_getAjaxUrlForOperation(operation) {
		const l10n = window.wlfmc_l10n || {};

		const URL_MAP = {
			// General AJAX URL (wlfmc_l10n.ajax_url)
			add_to_wishlist: 'ajax_url',
			remove_from_wishlist: 'ajax_url',
			delete_item: 'ajax_url',
			update_item_quantity: 'ajax_url',
			change_layout: 'ajax_url',
			gdpr_action: 'ajax_url',
			load_automations: 'ajax_url',
			remove_from_waitlist: 'ajax_url',
			remove_from_list: 'ajax_url',

			// Admin AJAX URL (wlfmc_l10n.admin_url)
			add_to_cart: 'admin_url',
			sort_items: 'admin_url',
			move_to_list: 'admin_url',
			copy_to_list: 'admin_url',

			// Waitlist AJAX URL (wlfmc_l10n.waitlist_ajax_url)
			add_to_waitlist: 'waitlist_ajax_url',

			// Multi-list AJAX URL (wlfmc_l10n.multi_list_ajax_url)
			add_to_list: 'multi_list_ajax_url',
			create_list: 'multi_list_ajax_url',
			delete_list: 'multi_list_ajax_url',
			update_list: 'multi_list_ajax_url',
			change_privacy: 'multi_list_ajax_url',
			load_lists: 'multi_list_ajax_url',

			// Save for Later AJAX URL (wlfmc_l10n.save_for_later_ajax_url)
			add_to_save_for_later: 'save_for_later_ajax_url',
			remove_from_save_for_later: 'save_for_later_ajax_url',
			save_for_later_add_to_cart: 'save_for_later_ajax_url',
			add_all_to_save_for_later: 'save_for_later_ajax_url',
		};

		const urlKey = URL_MAP[operation];

		if (urlKey && l10n[urlKey]) {
			return l10n[urlKey];
		}

		// Fallback: prefer ajax_url, then admin_url
		return l10n.ajax_url || l10n.admin_url || '';
	}

	_buildHeaders() {
		const l10n = window.wlfmc_l10n || {};
		const headers = {};
		if (l10n.ajax_mode === 'rest_api' && l10n.nonce) {
			headers['X-WP-Nonce'] = l10n.nonce;
		}
		return headers;
	}

	_setupOnlineListeners() {
		window.addEventListener('online', () => {
			eventBus.emit(EVENTS.OFFLINE_STATUS_CHANGED, { online: true });
			this._processOfflineBuffer();
		});
		window.addEventListener('offline', () => {
			eventBus.emit(EVENTS.OFFLINE_STATUS_CHANGED, { online: false });
		});
	}

	_resolveScope(operation, data) {
		// Allow explicit scope override
		if (data instanceof FormData) {
			const explicitScope = data.get('_scope');
			if (explicitScope) return String(explicitScope);
		} else if (typeof data === 'object' && data !== null && data._scope) {
			return String(data._scope);
		}

		let productId = null;
		let listId = null;
		let lists = null;
		let cartItemKey = null;
		let itemId = null;
		const isMerge = window.wlfmc_l10n?.merge_lists === 'true';

		if (data instanceof FormData) {
			productId = data.get('add_to_wishlist')
				|| data.get('remove_from_wishlist')
				|| data.get('add_to_waitlist')
				|| data.get('product_id')
				|| data.get('add_to_list');
			listId = data.get('wishlist_id');
			lists = data.get('wishlist_ids');
			if (lists) {
				try { lists = JSON.parse(lists); } catch { lists = [lists]; }
				if (!Array.isArray(lists)) lists = [lists];
			}
			cartItemKey = data.get('cart_item_key') || data.get('add_to_save_for_later');
			itemId = data.get('item_id');
		} else if (typeof data === 'object' && data !== null) {
			productId = data.product_id
				|| data.add_to_wishlist
				|| data.remove_from_wishlist
				|| data.add_to_waitlist
				|| data.add_to_list;
			lists = Array.isArray(data.wishlist_ids) ? data.wishlist_ids : (data.wishlist_ids ? [data.wishlist_ids] : null);
			listId = data.wishlist_id;
			cartItemKey = data.cart_item_key || data.add_to_save_for_later;
			itemId = data.item_id;
		}

		switch (operation) {
			case 'add_to_wishlist':
			case 'remove_from_wishlist':
			case 'delete_item':
				return ScopeHelper.wishlist(productId, listId, isMerge);
			case 'add_to_waitlist':
			case 'remove_from_waitlist':
				return ScopeHelper.waitlist(productId);
			case 'add_to_list':
			case 'remove_from_list':
				if (lists && lists.length > 1) return ScopeHelper.multiListBatch(productId);
				if (lists && lists.length === 1) return ScopeHelper.multiList(productId, lists[0]);
				return ScopeHelper.multiList(productId, listId);
			case 'move_to_list':
				return ScopeHelper.multiListMove(productId || itemId, data?.source_list_id || data?.current_list, data?.dest_list_id || data?.destination_wishlist_id);
			case 'copy_to_list':
				return ScopeHelper.multiListCopy(productId || itemId, data?.source_list_id || data?.current_list, data?.dest_list_id || data?.wishlist);
			case 'create_list':
				return ScopeHelper.multiListManage('create');
			case 'update_list':
			case 'delete_list':
			case 'change_privacy':
				return ScopeHelper.multiListManage(operation, listId);
			case 'add_to_save_for_later':
				return ScopeHelper.sfl(cartItemKey, productId);
			case 'remove_from_save_for_later':
			case 'save_for_later_add_to_cart':
				return ScopeHelper.sflItem(itemId || cartItemKey || 'unknown');
			case 'add_all_to_save_for_later':
				return ScopeHelper.sflBatch();
			case 'update_item_quantity':
				if (data?._table_scope === 'save_for_later' || data?.table_scope === 'save_for_later') {
					return ScopeHelper.sfl(cartItemKey, productId);
				}
				return ScopeHelper.wishlist(productId, listId, isMerge);
			default:
				return ScopeHelper.generic('core', operation, productId || listId || cartItemKey || itemId || 'global');
		}
	}

	/**
	 * Add operation with deduplication.
	 * If a previous operation for the same scope exists (in-flight or queued), it is cancelled.
	 */
	addWithDeduplication(operation, data, options = {}) {
		if (!this.initialized) this.init();
		const scope = this._resolveScope(operation, data);

		// 1. Abort any previous IN-FLIGHT HTTP request for this scope
		if (this._activeControllers.has(scope)) {
			const prevController = this._activeControllers.get(scope);
			prevController.abort();
			this._activeControllers.delete(scope);
			console.log(`[QueueManager] Aborted previous in-flight request for scope: ${scope}`);
		}

		// 2. Generate a unique ID for THIS task.
		// Any previous queued task for this scope will see a mismatched ID and abort itself.
		const taskId = ++this._taskCounter;
		this._currentTaskId.set(scope, taskId);

		if (!navigator.onLine) {
			return this._addToOfflineBuffer(operation, data, options, scope);
		}

		// 3. Create a new AbortController for THIS request
		const controller = new AbortController();
		this._activeControllers.set(scope, controller);

		// 4. Add to PQueue
		const promise = this.queue.add(() => {
			// Check if this task is still the latest for its scope
			if (this._currentTaskId.get(scope) !== taskId) {
				console.log(`[QueueManager] Task superseded for scope: ${scope}`);
				const err = new Error('Operation cancelled');
				err.name = 'AbortError';
				throw err;
			}
			return this._executeWithRetry(operation, data, options, controller, scope);
		});

		const finalPromise = promise.finally(() => {
			// Only delete controller if it's still ours
			if (this._activeControllers.get(scope) === controller) {
				this._activeControllers.delete(scope);
			}
			// Clean up task ID if it's still ours
			if (this._currentTaskId.get(scope) === taskId) {
				this._currentTaskId.delete(scope);
			}
		});

		// Prevent "Uncaught (in promise)" warnings when the operation is aborted/superseded
		finalPromise.catch(() => {});

		return promise;
	}

	/**
	 * Explicitly cancel any in-flight or queued operation for a scope.
	 * Used by ConflictManager when a delete arrives while add is in-flight/queued.
	 */
	cancelByScope(scope) {
		// Abort in-flight
		if (this._activeControllers.has(scope)) {
			this._activeControllers.get(scope).abort();
			this._activeControllers.delete(scope);
			console.log(`[QueueManager] Explicitly cancelled in-flight for scope: ${scope}`);
		}
		// Invalidate queued task by incrementing the counter for this scope
		this._currentTaskId.set(scope, ++this._taskCounter);
	}

	/**
	 * Alias for backward compatibility
	 */
	abortByScope(scope) {
		this.cancelByScope(scope);
	}

	async _executeWithRetry(operation, data, options, controller, scope, attempt = 1) {
		try {
			let result;
			if (data instanceof FormData) {
				result = await this._executeOperationWithFormData(operation, data, controller);
			} else {
				result = await this._executeOperation(operation, data, controller);
			}
			return this.normalizeResponse(result);
		} catch (error) {
			if (error.name === 'AbortError') {
				console.log(`[QueueManager] Operation ${operation} aborted for scope ${scope}`);
				throw error;
			}
			if (error.status === 401 || error.status === 403) {
				showError(window.wlfmc_l10n?.labels?.session_expired);
				const myAccountUrl = window.wlfmc_l10n?.login_url;
				if (window.location) window.location.href = myAccountUrl;
				throw error;
			}
			if (this._isNetworkError(error) && attempt <= QUEUE_CONFIG.maxRetries) {
				const delay = this._calculateBackoff(attempt);
				eventBus.emit(EVENTS.ACTION_RETRY, { operation, attempt, delay, scope });
				await new Promise(r => setTimeout(r, delay));
				return this._executeWithRetry(operation, data, options, controller, scope, attempt + 1);
			}
			throw error;
		}
	}

	_isNetworkError(error) {
		const message = (error.message || '').toLowerCase();
		return (
			error.status === 0 ||
			error.code === 'ECONNABORTED' ||
			message.includes('networkerror') ||
			message.includes('failed to fetch') ||
			message.includes('network request failed') ||
			message.includes('load failed')
		);
	}

	_calculateBackoff(attempt) {
		if (QUEUE_CONFIG.backoff === 'exponential') {
			return Math.min(QUEUE_CONFIG.retryDelay * Math.pow(2, attempt - 1), QUEUE_CONFIG.maxDelay);
		}
		return Math.min(QUEUE_CONFIG.retryDelay * attempt, QUEUE_CONFIG.maxDelay);
	}

	async _executeOperationWithFormData(operation, formData, controller) {
		const action = this._buildActionMap()[operation];
		if (!action) throw new Error(`Unknown operation: ${operation}`);

		if (!formData.has('action')) formData.append('action', action);
		if (!formData.has('context')) formData.append('context', 'frontend');

		const nonce = window.wlfmc_l10n?.nonce || '';
		if (nonce && !formData.has('nonce')) formData.append('nonce', nonce);

		const headers = this._buildHeaders();

		// Use operation-specific URL to match legacy endpoints exactly
		const url = this._getAjaxUrlForOperation(operation);

		const fetchOptions = {
			method: 'POST',
			body: formData,
			signal: controller.signal,
			credentials: 'same-origin',
			keepalive: true,
		};

		if (Object.keys(headers).length > 0) fetchOptions.headers = headers;

		const response = await fetch(url, fetchOptions);

		if (!response.ok) {
			const error = new Error(`HTTP error ${response.status}`);
			error.status = response.status;
			throw error;
		}

		const contentType = response.headers.get('content-type') || '';
		if (!contentType.includes('application/json')) {
			const text = await response.text();
			console.error('[QueueManager] Non-JSON response:', text.substring(0, 500));
			throw new Error('Server returned non-JSON response');
		}

		return await response.json();
	}

	async _executeOperation(operation, data, controller) {
		const action = this._buildActionMap()[operation];
		if (!action) throw new Error(`Unknown operation: ${operation}`);
		const formData = new FormData();
		formData.append('action', data.action || action);
		formData.append('context', data.context || 'frontend');
		const nonce = data.nonce;
		if (nonce) formData.append('nonce', nonce);
		Object.entries(data).forEach(([key, value]) => {
			if (key === 'action' || key === 'context' || key === 'nonce' || key === '_scope') return;
			if (value === null || value === undefined) return;
			if (Array.isArray(value)) {
				value.forEach(item => formData.append(key + '[]', String(item)));
			} else if (typeof value === 'object') {
				formData.append(key, JSON.stringify(value));
			} else {
				formData.append(key, String(value));
			}
		});
		return this._executeOperationWithFormData(operation, formData, controller);
	}

	// === Offline Buffer ===

	_loadOfflineBuffer() {
		try {
			const stored = localStorage.getItem(OFFLINE_STORAGE_KEY);
			if (stored) {
				this._offlineBuffer = JSON.parse(stored);
				if (!Array.isArray(this._offlineBuffer)) this._offlineBuffer = [];
			} else {
				this._offlineBuffer = [];
			}
			// FIX: Clear pending promises on load. A page reload means the original
			// JS execution context (and its Promises) is permanently gone.
			this._pendingOfflinePromises.clear();
		} catch {
			this._offlineBuffer = [];
			this._pendingOfflinePromises.clear();
		}
	}

	_saveOfflineBuffer() {
		try {
			localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(this._offlineBuffer));
		} catch (e) {
			console.warn('[QueueManager] Failed to save offline buffer', e);
		}
	}

	_addToOfflineBuffer(operation, data, options, scope) {
		return new Promise((resolve, reject) => {
			const serializableData = data instanceof FormData ? this._serializeFormData(data) : data;
			const timestamp = Date.now();

			// FIX: Store only serializable data in the buffer object.
			const item = {
				operation,
				data: serializableData,
				options,
				scope,
				timestamp,
			};

			// FIX: Store Promise handlers in a separate in-memory Map.
			// This prevents JSON.stringify from silently stripping them.
			this._pendingOfflinePromises.set(timestamp, { resolve, reject });

			this._offlineBuffer.push(item);
			this._saveOfflineBuffer();
			eventBus.emit(EVENTS.OFFLINE_STATUS_CHANGED, { online: false });
			console.log('[QueueManager] Added to offline buffer:', operation);
		});
	}

	_serializeFormData(formData) {
		const obj = {};
		for (const [key, value] of formData.entries()) obj[key] = value;
		return { __formData: true, data: obj };
	}

	_deserializeToFormData(serialized) {
		if (serialized.__formData) {
			const formData = new FormData();
			Object.entries(serialized.data).forEach(([key, value]) => formData.append(key, value));
			return formData;
		}
		return serialized;
	}

	async _processOfflineBuffer() {
		if (this._offlineBuffer.length === 0 || !navigator.onLine) return;
		const items = [...this._offlineBuffer];
		this._offlineBuffer = [];
		this._saveOfflineBuffer();

		for (const item of items) {
			try {
				const data = this._deserializeToFormData(item.data);
				const result = await this.addWithDeduplication(item.operation, data, item.options);

				// FIX: Safely retrieve and call the in-memory handler if it still exists.
				const handlers = this._pendingOfflinePromises.get(item.timestamp);
				if (handlers) {
					handlers.resolve(result);
					this._pendingOfflinePromises.delete(item.timestamp);
				}
			} catch (error) {
				if (!navigator.onLine) {
					this._offlineBuffer.push(item);
					this._saveOfflineBuffer();
				} else {
					// FIX: Safely retrieve and call the in-memory handler if it still exists.
					const handlers = this._pendingOfflinePromises.get(item.timestamp);
					if (handlers) {
						handlers.reject(error);
						this._pendingOfflinePromises.delete(item.timestamp);
					}
				}
			}
		}

		if (this._offlineBuffer.length > 0) {
			setTimeout(() => this._processOfflineBuffer(), 5000);
		}
	}

	/**
	 * Normalize server response.
	 * Error messages come from the server response (already translated).
	 * Fallback uses wlfmc_l10n.labels.operation_failed (also translated).
	 */
	normalizeResponse(result) {
		if (result && typeof result === 'object') {
			if ('success' in result) {
				if (result.success === true) {
					return result.data !== undefined ? result.data : result;
				} else {
					const errorMsg = result.data?.message
						|| result.message
						|| window.wlfmc_l10n?.labels?.operation_failed;
					const error = new Error(errorMsg);
					error.status = result.data?.status || 500;
					throw error;
				}
			}
			if ('result' in result) {
				if (result.result === 'true' || result.result === true || result.result === 'exists') {
					return result;
				} else if (result.result === 'false' || result.result === false) {
					const errorMsg = result.message
						|| window.wlfmc_l10n?.labels?.operation_failed;
					const error = new Error(errorMsg);
					error.status = 500;
					throw error;
				}
			}
			return result;
		}
		return result;
	}

	getStatus() {
		return {
			isProcessing: this.queue?.size > 0 || this.queue?.pending > 0,
			offlineItems: this._offlineBuffer.length,
			queueSize: this.queue?.size || 0,
			pending: this.queue?.pending || 0,
			activeControllers: this._activeControllers.size,
		};
	}

	clearOfflineBuffer() {
		this._offlineBuffer = [];
		this._saveOfflineBuffer();
		this._pendingOfflinePromises.clear();
	}
}

export const queueManager = new QueueManager();
export default queueManager;
